import React, { useContext, useReducer, useState } from "react";
import { Col, FormControl, InputGroup, Modal, Row } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import { toast } from "react-toastify";
import useAxios from "../hooks/useAxios";
import useTableUsers from "../hooks/useTableUsers";
import { Store } from "../Store";
import { getError } from "../utils";
import AlertBox from "./AlertBox";
import LoadingBox from "./LoadingBox";
import MessageBox from "./MessageBox";

// Reducer for managing API state
const reducer = (state, action) => {
  switch (action.type) {
    case "REQUEST_START":
      return { ...state, loading: true };
    case "REQUEST_SUCCESS":
      return { ...state, loading: false };
    case "REQUEST_FAIL":
      return { ...state, loading: false, error: action.payload };
    case "RESET_ERROR":
      return { ...state, error: "" };
    default:
      return state;
  }
};

export default function TableUsersModal({
  showTableUsersModal,
  setShowTableUsersModal,
  handleSelect,
}) {
  const axios = useAxios();
  const [{ loading, error }, dispatch] = useReducer(reducer, {
    loading: false,
    error: "",
  });

  const [, , setLoadTableUsers] = useTableUsers();
  const { state } = useContext(Store);
  const { tableUsers } = state;

  const [mode, setMode] = useState("view"); // "view", "add", "edit"
  const [selectedUser, setSelectedUser] = useState({});
  const [name, setName] = useState("");

  // Handlers
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || name.length < 2) {
      toast.error(
        "Lütfen ad alanını en az 2 karakter olacak şekilde doldurunuz!"
      );
      return;
    }

    try {
      dispatch({ type: "REQUEST_START" });

      if (mode === "add") {
        await axios.post("/api/tableusers", { name });
        toast.success("Kullanıcı eklendi!");
      } else if (mode === "edit") {
        await axios.put(`/api/tableusers/${selectedUser._id}`, { name });
        toast.success("Kullanıcı güncellendi!");
      }

      setMode("view");
      setName("");
      setSelectedUser({});
      setLoadTableUsers(true);
      dispatch({ type: "REQUEST_SUCCESS" });
    } catch (err) {
      toast.error(getError(err));
      dispatch({ type: "REQUEST_FAIL", payload: getError(err) });
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm("Silmek istediğinize emin misiniz?")) return;
    try {
      dispatch({ type: "REQUEST_START" });
      await axios.delete(`/api/tableusers/${user._id}`);
      toast.success("Kullanıcı silindi!");
      setLoadTableUsers(true);
      dispatch({ type: "REQUEST_SUCCESS" });
    } catch (err) {
      toast.error(getError(err));
      dispatch({ type: "REQUEST_FAIL", payload: getError(err) });
    }
  };

  const handleCancel = () => {
    setMode("view");
    setName("");
    setSelectedUser({});
  };

  // Sub-components
  const UserListItem = ({ user }) => (
    <li
      className="list-group-item d-flex justify-content-between align-items-center hover-effect"
      onClick={() => {
        handleSelect(user);
        setShowTableUsersModal(false);
      }}
    >
      {mode === "edit" && selectedUser._id === user._id ? (
        <InputGroup>
          <FormControl
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Bir isim gir."
          />
          <Button variant="primary" onClick={handleSubmit}>
            Kaydet
          </Button>
          <Button variant="outline-danger" onClick={handleCancel}>
            İptal
          </Button>
        </InputGroup>
      ) : (
        <>
          <span style={{ flex: 1 }}>{user.name}</span>
          <div>
            <Button
              variant="outline-success"
              onClick={() => {
                setMode("edit");
                setSelectedUser(user);
                setName(user.name);
              }}
              disabled={loading}
            >
              <i className="fas fa-edit"></i>
            </Button>{" "}
            <Button
              variant="outline-danger"
              onClick={() => handleDelete(user)}
              disabled={loading}
            >
              <i className="fas fa-trash-alt"></i>
            </Button>
          </div>
        </>
      )}
    </li>
  );

  return (
    <div>
      <Modal
        show={showTableUsersModal}
        onHide={() => setShowTableUsersModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Masalara Verilebilecek İsimler</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loading && <LoadingBox />}
          {error && <MessageBox variant="danger">{error}</MessageBox>}
          <Row>
            <Col>
              {mode === "add" ? (
                <InputGroup>
                  <FormControl
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Bir isim gir."
                  />
                  <Button variant="primary" onClick={handleSubmit}>
                    Kaydet
                  </Button>
                  <Button variant="outline-danger" onClick={handleCancel}>
                    İptal
                  </Button>
                </InputGroup>
              ) : (
                <Button
                  variant="outline-primary"
                  onClick={() => setMode("add")}
                  disabled={loading}
                >
                  <i className="fas fa-plus"></i> Yeni
                </Button>
              )}
            </Col>
          </Row>
          <ul
            className="list-group mt-3 hover-list"
            style={{ maxHeight: "400px", overflowY: "auto" }}
          >
            {tableUsers?.length > 0 ? (
              tableUsers.map((user) => (
                <UserListItem key={user._id} user={user} />
              ))
            ) : (
              <AlertBox primary>
                <span>
                  Kayıt bulunamadı. Yeni kayıt oluşturmak için artı butonuna
                  basınız!
                </span>
              </AlertBox>
            )}
          </ul>
        </Modal.Body>
      </Modal>

      <style>
        {`.hover-effect {
  cursor: pointer;
  transition: background-color 0.3s ease, transform 0.2s ease;
}

.hover-effect:hover {
  background-color: #f8f9fa; /* Light gray background on hover */
  transform: scale(1.02); /* Slight enlargement */
}

.hover-list li {
  transition: all 0.3s ease;
}

.hover-list li:hover {
  background-color: #e9ecef; /* Highlighted background */
  transform: translateY(-2px); /* Slight upward motion */
}
`}
      </style>
    </div>
  );
}
